import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Building2, Users, User, Armchair, Bell, Shield } from "lucide-react";
import { cn } from "@/lib/utils";

interface SettingsLayoutProps {
  children: ReactNode;
}

const SETTINGS_TABS = [
  {
    value: "workspace",
    label: "Workspace",
    icon: Building2,
    path: "/settings/workspace",
  },
  {
    value: "team",
    label: "Team",
    icon: Users,
    path: "/settings/team",
  },
  {
    value: "seat",
    label: "Mein Seat",
    icon: Armchair,
    path: "/settings/seat",
  },
  {
    value: "profile",
    label: "Profil",
    icon: User,
    path: "/settings/profile",
  },
  {
    value: "security",
    label: "Sicherheit",
    icon: Shield,
    path: "/settings/security",
  },
  {
    value: "notifications",
    label: "Benachrichtigungen",
    icon: Bell,
    path: "/settings/notifications",
  },
];

export const SettingsLayout = ({ children }: SettingsLayoutProps) => {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-background pb-safe">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center gap-2 md:gap-4 mb-2 md:mb-4">
            <NavLink to="/dashboard">
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 md:px-3"
              >
                <ArrowLeft className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Zurück zum Dashboard</span>
              </Button>
            </NavLink>
          </div>

          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Einstellungen</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-0.5 md:mt-1">
              Verwalte deine Workspace- und persönlichen Einstellungen
            </p>
          </div>
        </div>
      </header>

      {/* Tabs Navigation - Mobile optimized */}
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-[65px] md:top-[89px] z-40">
        <div className="container max-w-7xl mx-auto">
          <nav className="flex overflow-x-auto scrollbar-hide px-4 md:px-6 -mb-px">
            {SETTINGS_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = location.pathname === tab.path;
              
              return (
                <NavLink
                  key={tab.value}
                  to={tab.path}
                  className={cn(
                    "flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-3 border-b-2 transition-colors whitespace-nowrap min-h-[48px] touch-manipulation flex-shrink-0",
                    isActive 
                      ? "border-primary text-foreground font-medium" 
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{tab.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="container max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {children}
      </main>
    </div>
  );
};
